import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Link,
  TextField,
  Typography,
  Paper,
  Grid,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router";
import imgUrl from '../assets/images/imgUrl';
import {
  sendLoginToken,
  validateLoginToken,
  setOtpEmail,
  setOtpSent,
  clearOtpState,
  setOtpError
} from '../store/slices/authSlice';
import Swal from 'sweetalert2';

const Otp = () => {
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Redux state
  const {
    otpEmail,
    otpSent,
    otpLoading,
    otpError,
    isAuthenticated
  } = useSelector((state) => state.auth);

  // Handle authentication state changes
  useEffect(() => {
    if (isAuthenticated) {
      // User is already logged in, redirect to dashboard
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Handle resend timer
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60); // 60 seconds
  };

  // Validate email format
  const isValidEmail = (email) => {
    return /\S+@\S+\.\S+/.test(email);
  };

  // Handle send OTP
  const handleSendOtp = async () => {
    if (!email.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your email address',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    if (!isValidEmail(email)) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid email address',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      // Clear any previous errors
      dispatch(setOtpError(null));
      
      // Set OTP email in state
      dispatch(setOtpEmail(email));
      
      // Send OTP
      const result = await dispatch(sendLoginToken(email));
      
      if (sendLoginToken.fulfilled.match(result)) {
        Swal.fire({
          title: 'Success',
          text: 'OTP sent successfully! Please check your email.',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          allowOutsideClick: true,
          buttonsStyling: false,
        });
        
        // Start resend timer
        startResendTimer();
      } else {
        throw new Error(result.payload?.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to send OTP. Please try again.',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    
    try {
      dispatch(setOtpError(null));
      
      const result = await dispatch(sendLoginToken(email));
      
      if (sendLoginToken.fulfilled.match(result)) {
        Swal.fire({
          title: 'Success',
          text: 'OTP resent successfully!',
          icon: 'success',
          showConfirmButton: true,
          confirmButtonText: 'OK',
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          allowOutsideClick: true,
          buttonsStyling: false,
        });
        
        startResendTimer();
      } else {
        throw new Error(result.payload?.message || 'Failed to resend OTP');
      }
    } catch (error) {
      console.error('Error resending OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Failed to resend OTP. Please try again.',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    }
  };

  // Handle verify OTP
  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter the OTP',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    if (otp.length < 4) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid OTP',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
      return;
    }

    try {
      dispatch(setOtpError(null));
      
      const result = await dispatch(validateLoginToken({ email, otp: otp }));
      
      if (validateLoginToken.fulfilled.match(result)) {
        // OTP validation successful, user will be redirected to dashboard
        // The useEffect above will handle the navigation
        console.log('OTP validation successful');
      } else {
        throw new Error(result.payload?.message || 'OTP validation failed');
      }
    } catch (error) {
      console.error('Error validating OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.message || 'Invalid OTP. Please try again.',
        icon: 'error',
        showConfirmButton: true,
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
      });
    }
  };

  // Handle back navigation
  const handleBack = () => {
    dispatch(clearOtpState());
    navigate(-1);
  };

  return (
    <Box
      sx={{
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: "hidden"
      }}
      className="LoginSection"
    >
      <Paper elevation={5} sx={{ p: 4, width: { xs: 320, sm: 360 }, textAlign: 'center', borderRadius: 2 }} className='UserSubSection'>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            mb: 2
          }}
        >
          {/* Back Button */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              cursor: 'pointer',
            }}
            onClick={handleBack}
          >
            <img src={imgUrl.backButtonIcon} alt="Back" width="24" />
          </Box>

          {/* Centered Logo */}
          <img src={imgUrl.peaceMovImg} alt="Logo" width="100" />
        </Box>

        {/* Heading */}
        <Typography variant="h6" fontWeight="400" mb={0} className='loginHeading'>
          LOGIN WITH OTP
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          Login for peacekeeper only
        </Typography>

        {/* Error Alert */}
        {otpError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {otpError}
          </Alert>
        )}

        {/* Email Section */}
        <Typography sx={{ textAlign: "left", mb: 1 }}>Email Address</Typography>
        <TextField
          fullWidth
          label=""
          placeholder='Please enter email'
          type="email"
          required
          variant="outlined"
          margin="dense"
          size="small"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={otpSent}
          sx={{ mb: 2 }}
        />

        {/* Send OTP Button */}
        <Button
          fullWidth
          variant="contained"
          onClick={handleSendOtp}
          disabled={otpLoading || otpSent}
          startIcon={otpLoading ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            mt: 2,
            background: 'linear-gradient(to right, #214F7D, #3A8139)',
            color: 'white',
            borderRadius: "50px"
          }}
        >
          {otpLoading ? 'Sending...' : otpSent ? 'OTP Sent' : 'Send OTP'}
        </Button>

        {/* OTP Verification Section - Only show after OTP is sent */}
        {otpSent && (
          <>
            <Typography sx={{ textAlign: "left", marginTop: "35px", mb: 1 }}>Verify OTP</Typography>
            <TextField
              fullWidth
              label=""
              placeholder='Enter OTP Here'
              type="number"
              required
              variant="outlined"
              margin="dense"
              size="small"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Grid container spacing={2} sx={{ mb: 2 }}>
              {/* Resend OTP Button */}
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleResendOtp}
                  disabled={otpLoading || resendTimer > 0}
                  sx={{
                    background: 'linear-gradient(to right, #60778E, #6A8D66)',
                    color: 'white',
                    borderRadius: "50px",
                    fontSize: '0.8rem'
                  }}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
                </Button>
              </Grid>
              
              {/* Verify OTP Button */}
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading || !otp.trim()}
                  startIcon={otpLoading ? <CircularProgress size={20} color="inherit" /> : null}
                  sx={{
                    background: 'linear-gradient(to right, #214F7D, #3A8139)',
                    color: 'white',
                    borderRadius: "50px"
                  }}
                >
                  {otpLoading ? 'Verifying...' : 'Verify OTP'}
                </Button>
              </Grid>
            </Grid>
          </>
        )}

        {/* Register Link */}
        {!window.cordova && (
          <Link 
            href="#" 
            underline="hover" 
            sx={{ display: 'block', mt: 3, fontSize: 14, textDecoration: "underline" }} 
            onClick={() => navigate("/register")}
          >
            Register as Peacekeeper
          </Link>
        )}
      </Paper>
    </Box>
  );
};

export default Otp;
